namespace Solovis {

    export function getGlobalObject(): any {
        return typeof window != "undefined" ? window : global;
    }

    /**
     * Setup a namespace within Solovis hierarchy if it is not already defined.
     */
    export function defineNamespace(namespace: string, root?: any): any {      
        if (!root) {
            root = getGlobalObject();
        }

        var parts = namespace.split('.');
        var current = root;
        for (var i = 0; i < parts.length; i++) {
            var part = parts[i];
            if (current[part]) {
                current = current[part];
            }
            else {
                var newNamespace = { __namespace: true};
                current[part] = newNamespace;
                current = newNamespace;
            }
        }

        return current;
    }

    /**
     * Create a class. fullClassName is an optional "Namespace1.ChildNamespace.TypeName"-style class path.
     */    
    export function defineClass(fullClassName: string, baseType: any, construct: any): any {       
        if (fullClassName && typeof(fullClassName) !== "string") {
            throw "fullClassName must be a string";
        }                

        var newType = function (options) {
            if (!this.construct) {
                throw "Missing construct method: " + fullClassName;
            }
            this.construct(options);
        };

        if (baseType) {            
            newType.prototype = Object.create(baseType.prototype);
            newType.prototype.constructor = newType;

            if (baseType.__typeName) {
                newType.prototype.__parentTypeName = baseType.__typeName;                
            }
        }

        newType.prototype.__typeName = fullClassName ? fullClassName : null;

        // Setup constructor.
        newType.prototype.construct = function (options) {
            if (baseType && baseType.prototype.construct) {
                baseType.prototype.construct.call(this, options);
            }
            else if (baseType) {
                // This is a TypeScript base class.
                baseType.call(this, options);
            }

            if (construct) {
                construct.call(this, options);
            }
        };

        // Assign to namespace/class.
        if (fullClassName) {
            var lastIndex = fullClassName.lastIndexOf(".");
            var ns = fullClassName.substr(0, lastIndex);
            var typeName = fullClassName.substr(lastIndex + 1);

            var nsObject = Solovis.defineNamespace(ns);

            if (nsObject[typeName]) {
                console.log("WARNING: Object or namespace is being overwritten by new class definition: " + fullClassName);
            }

            nsObject[typeName] = newType;
        }

        newType.prototype.__type = newType;

        return newType;
    }


    /**
     * If url starts with "~/", this will convert site relative url to server relative. Otherwise, it
     * returns the original url. Solovis.Info.siteRoot should contain a trailing slash.
     */
    export function resolveUrl(url: string, siteRootOverride?: string): string {
        let siteRootToUse: string = siteRootOverride ? siteRootOverride : (Solovis as any).Info.siteRoot;

        if (url && url.length >= 2 && url[0] == '~' && url[1] == '/') {
            if (url.length == 2) {
                return siteRootToUse;
            }
            else {
                let newUrl = siteRootToUse;
                if (newUrl[newUrl.length - 1] != "/") {
                    newUrl += "/";
                }
                newUrl += url.substr(2);
                return newUrl;
            }
        }

        return url;
    }

    /**
     * Returns true if url is for an external domain.
     */
    export function isExternalUrl(url: string): boolean {
        if (!url) {
            return false;
        }

        let lowerCaseUrl = url.toLowerCase();
        if (!isUrlAbsolute(lowerCaseUrl)) {
            return false;
        }

        let urlDomain = lowerCaseUrl.replace('http://', '').replace('https://', '').split('/')[0];
        let currentDomain = location.href.toLowerCase().replace('http://', '').replace('https://', '').split('/')[0];

        return urlDomain !== currentDomain;
    }

    /**
     * Returns true if url is absolute.
     */
    function isUrlAbsolute(url: string) {
        if (url.indexOf("https://") === 0) {
            return true;
        }

        if (url.indexOf("http://") === 0) {
            return true;
        }

        return false;
    }

    /**
     * If url starts with "~/", this will convert site relative url to server relative. This will include
     * the http scheme, domain, and port.
     */
    export function resolveFullUrl(url: string): string {      
        var resolvedUrl = Solovis.resolveUrl(url);

        var urlPrefix = window.location.origin;
        if (!urlPrefix) {
            // protocol should be "http:" or "https:". host will be "server-dns-name:port"
            urlPrefix = window.location.protocol + "//" + window.location.host;
        }

        if (urlPrefix[urlPrefix.length - 1] != '/') {
            if (resolvedUrl && resolvedUrl[0] != '/') {
                urlPrefix += "/";
            }
        }

        var fullUrl = urlPrefix + resolvedUrl;

        return fullUrl;
    }


    /**
     * All functions on baseClassObject prototype will be copied to classObject prototype.
     */
    export function inherit(classObject: any, baseClassObject: any): void {      
        var classPrototype = classObject.prototype;
        var basePrototype = baseClassObject.prototype;
        for (var member in basePrototype) {
            var definition = basePrototype[member];
            if (typeof definition == "function") {
                classPrototype[member] = definition;
            }
        }
    }

    /**
     * This is an old logging wrapper. console.log is preferred since it provides more useful contextual information
     * in browser debugging consoles.
     */
    export function log(msg: any): void {
        if (msg && msg.message && msg.stack) {
            console.log(msg.stack);
        }
        else {
            console.log(msg);
        }
    }

    /**
     * Import javascript reference and call callback after it is loaded.
     */
    export function importScript(src: string, callback: any): void {
        var scriptUrl = Solovis.resolveUrl(src);
        
        $LAB.script(scriptUrl).wait(function () {
            if (callback) {                
                callback();
            }                
        });       
    }

    /**
     * Import stylesheet reference and call callback after it is loaded.
     */
    export function importStylesheet(href: string, callback: any): void {
        var sheetHref = Solovis.resolveUrl(href);

        var sheet = document.createElement("link");
        sheet.setAttribute("rel", "stylesheet");
        sheet.setAttribute("type", "text/css");
        sheet.setAttribute("href", sheetHref);

        if (callback)
            callback = _.once(callback);

        // TODO: Investigate reliable means of doing following.

        if ((window as any).attachEvent && document.all) {
            // IE does not support the onload event.
            (sheet as any).onreadystatechange = function () {
                if (this.readyState === "complete" || this.readyState === "loaded") {
                    if (callback)
                        callback();
                }
            };
        }
        else {
            sheet.onload = callback;
        }

        document.getElementsByTagName("head")[0].appendChild(sheet);
    }

    /**
     * Resolves an object by its string path relative to a root object.
     */
    export function resolveObject(path: string, root?: any): any {        
        if (!root) {
            root = window;
        }

        var parts = path.split('.');
        var current = root;
        for (var i = 0; i < parts.length; i++) {
            var part = parts[i];
            var currentPart = current[part];

            if (typeof currentPart != "undefined" && currentPart !== null) {
                current = currentPart;
            }
            else {
                // Object not found.
                return null;
            }
        }

        return current;
    }


    /**
     * setObjectValueByPath will set a value using a dotted path string.
     */
    export function setObjectValueByPath (object: any, path: string, value: any): void {
        if (!object) {
            throw "object not set";
        }

        var parts = path.split('.');
        var current = object;
        for (var i = 0; i < parts.length - 1; i++) {
            var part = parts[i];
            var currentPart = current[part];

            if (typeof currentPart != "undefined" && currentPart !== null) {
                current = currentPart;
            }
            else {
                currentPart = {};
                current[part] = currentPart;
                current = currentPart;
            }
        }

        var finalPart = parts[parts.length - 1];
        current[finalPart] = value;
    }


    export function deepCopy(src: any): any {
        if (src === null || typeof src == "undefined") {
            return null;
        }
        else if (typeof src == "object") {
            if (Object.prototype.toString.call(src) === "[object Array]") {
                // Copy array.
                var newArray = [];
                for (var i = 0; i < src.length; i++) {
                    var itemCopy = Solovis.deepCopy(src[i]);
                    newArray.push(itemCopy);
                }
                return newArray;
            }
            else {
                var copy = $.extend(true, {}, src);
                return copy;
            }
        }
        else {
            return src;
        }
    }

    /**
     * Returns true if obj is neither undefined nor null.
     */
    export function isNotNull(obj: any): boolean {
        return typeof obj !== "undefined" && obj !== null;        
    }

    /**
     * Returns true if obj is undefined or null.
     */
    export function isNull(obj: any): boolean {
        return typeof obj === "undefined" || obj === null;
    }

}
